import type { PromptUse } from '@/components/features/Editor';
import { AUTH_TOKEN_KEY } from '@/constant/localStorage';
import api, { handleError } from '@/services/axiosInstance';
import type { AgentStreamRoute } from '@/types/agentStream';
import storage from '@/utils/storage';

import { isUnknownSseEvent } from './sseFrame';

export type ConnieStreamChannel = 'global' | 'job';

const CONNIE_STREAM_URLS: Record<ConnieStreamChannel, string> = {
  global: 'https://api.vh3connect.io/api:f-Fs4_ed/connie/chat/unified/stream',
  job: 'https://api.vh3connect.io/api:f-Fs4_ed/connie/chat/job/unified/stream',
};

type ConnieStreamContext = {
  channel?: ConnieStreamChannel;
  sessionId?: string;
  contactId?: string;
  currentJobReference?: string;
};

export type ConnieTextStreamRequest = ConnieStreamContext & {
  message: string;
  /** Prompt Templates record of the message's chips (FSI #1234). Sent only when non-empty. */
  prompts?: PromptUse[];
};

export type ConnieVoiceStreamRequest = ConnieStreamContext & {
  file: Blob;
  fileName: string;
};

export type ConnieStreamRequest =
  | ConnieTextStreamRequest
  | ConnieVoiceStreamRequest;

export type ConnieToolCallOutput = {
  toolName: string;
  toolUseId: string;
  output: unknown;
};

export type ConnieTranscriptionEvent = {
  transcript: string;
  truncated?: boolean;
  language?: string;
  speakerCount?: number;
  summary?: string;
  mimeType?: string;
  transcriptionModel?: string;
};

export type ConnieStreamEvent =
  | { type: 'transcription'; data: ConnieTranscriptionEvent }
  | { type: 'summary_context'; data: unknown }
  | { type: 'progress'; data: { delta: string } }
  | { type: 'text_delta'; data: { delta?: string; text?: string } }
  | { type: 'tool_start'; data: { toolName: string; toolUseId: string } }
  | { type: 'tool_result'; data: ConnieToolCallOutput }
  | {
      type: 'done';
      data: {
        sessionId: string;
        toolsUsed?: string[];
        toolCallOutputs?: ConnieToolCallOutput[];
        usage?: unknown;
        route?: AgentStreamRoute;
        /** FSI's index for this turn in the session (FSI #1159). */
        turnIndex?: number;
        /** Library Documents the turn touched; parsed with parseLibraryReferences. */
        libraryReferences?: unknown;
        /** What filled the context window (FSI #1224); parsed with parseContextMap. */
        contextMap?: unknown;
      };
    }
  | { type: 'error'; data: { message: string } };

const CONNIE_API_URL = 'https://api.vh3connect.io/api:f-Fs4_ed';

export type CitationExcerpt = {
  documentId: string;
  name: string;
  page: number;
  citation: string;
  excerpt: string;
  highlight: string[];
};

/**
 * The passage behind one Page Citation of a stored Connie turn (Xano
 * `connie/sessions/{session_id}/citation`, FSI #1159). Xano fills the tenant and the
 * user from the JWT. FSI reads the page for this request only and stores nothing;
 * the result is never cached here either.
 */
export const getCitationExcerpt = async ({
  sessionId,
  turnIndex,
  documentId,
  page,
}: {
  sessionId: string;
  turnIndex: number;
  documentId: string;
  page: number;
}): Promise<CitationExcerpt | undefined> => {
  try {
    const { data } = await api.post<CitationExcerpt>(
      `${CONNIE_API_URL}/connie/sessions/${encodeURIComponent(sessionId)}/citation`,
      { turn_index: turnIndex, document_id: documentId, page }
    );
    return data;
  } catch (error) {
    handleError(error, 'getCitationExcerpt');
  }
};

export class ConnieStreamError extends Error {
  constructor(
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'ConnieStreamError';
  }
}

const isVoiceRequest = (
  request: ConnieStreamRequest
): request is ConnieVoiceStreamRequest => 'file' in request;

const getStatusMessage = (status: number) => {
  switch (status) {
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'This conversation is unavailable. Start a new conversation.';
    case 429:
      return 'Connie is at capacity. Please try again shortly.';
    case 503:
      return 'Connie is temporarily unavailable. Please try again later.';
    default:
      return `Connie stream failed: ${status}`;
  }
};

const getEventNameFromPayload = (data: unknown) => {
  if (!data || typeof data !== 'object') {
    return '';
  }

  const payload = data as Record<string, unknown>;
  const eventName = payload.event ?? payload.type;
  return typeof eventName === 'string' ? eventName : '';
};

const getEventData = (data: unknown) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const payload = data as Record<string, unknown>;
  return payload.data ?? data;
};

const isConnieEventName = (value: string) =>
  value === 'transcription' ||
  value === 'summary_context' ||
  value === 'progress' ||
  value === 'text_delta' ||
  value === 'tool_start' ||
  value === 'tool_result' ||
  value === 'done' ||
  value === 'error';

const getFrameData = (frame: string) => {
  const data = frame
    .split(/\r?\n/)
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice('data:'.length).trimStart())
    .join('\n');

  return data.startsWith('data:')
    ? data.slice('data:'.length).trimStart()
    : data;
};

const getXanoEventName = (frame: string) => {
  const match = /^event:\s*(.+?)\s*$/.exec(getFrameData(frame));
  const eventName = match?.[1] ?? '';
  return isConnieEventName(eventName) ? eventName : null;
};

const parseEvent = (
  frame: string,
  pendingEventName?: string | null
): ConnieStreamEvent | null => {
  let eventName = '';

  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
    }
  }

  const frameData = getFrameData(frame);
  if (!frameData) {
    return null;
  }

  // Xano's api.stream wraps a manually emitted SSE event inside its own data
  // frame. It preserves the inner `event:` line but strips the inner `data:`
  // prefix, yielding `event: transcription\n{...}`. Restore that prefix and
  // parse the normalized inner frame recursively.
  if (frameData.startsWith('event:') && frameData !== frame) {
    const [eventLine, ...payloadLines] = frameData.split(/\r?\n/);
    if (eventLine && payloadLines.length) {
      const normalizedPayload = payloadLines
        .map(line => (line.startsWith('data:') ? line : `data: ${line}`))
        .join('\n');
      return parseEvent(`${eventLine}\n${normalizedPayload}`, pendingEventName);
    }
  }

  // Unrecognised event names must not fail the stream, even when the
  // payload is not JSON.
  if (eventName && !isConnieEventName(eventName)) {
    return null;
  }

  let data: unknown;
  try {
    data = JSON.parse(frameData);
  } catch {
    throw new ConnieStreamError('Received an invalid Connie stream event.');
  }

  const payloadEventName = getEventNameFromPayload(data);
  if (!isConnieEventName(eventName) && payloadEventName) {
    eventName = payloadEventName;
  }
  if (!isConnieEventName(eventName) && pendingEventName) {
    eventName = pendingEventName;
  }
  data = getEventData(data);

  if (!eventName) {
    return null;
  }

  if (eventName === 'transcription') {
    return { type: eventName, data: data as ConnieTranscriptionEvent };
  }

  if (eventName === 'summary_context') {
    return { type: eventName, data };
  }

  if (eventName === 'progress') {
    if (!data || typeof data !== 'object') return null;
    const delta = (data as { delta?: unknown }).delta;
    if (typeof delta !== 'string' || !delta) return null;
    return { type: 'progress', data: { delta } };
  }

  if (eventName === 'text_delta') {
    return { type: eventName, data: data as { delta?: string; text?: string } };
  }

  if (eventName === 'tool_start') {
    return {
      type: eventName,
      data: data as { toolName: string; toolUseId: string },
    };
  }

  if (eventName === 'tool_result') {
    return { type: eventName, data: data as ConnieToolCallOutput };
  }

  if (eventName === 'done') {
    return {
      type: eventName,
      data: data as Extract<ConnieStreamEvent, { type: 'done' }>['data'],
    };
  }

  if (eventName === 'error') {
    return {
      type: eventName,
      data: data as { message: string },
    };
  }

  return null;
};

const getJsonErrorMessage = async (response: Response) => {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return getStatusMessage(response.status);
  }

  try {
    const body = (await response.json()) as {
      message?: string;
      error?: string;
    };
    return body.message || body.error || getStatusMessage(response.status);
  } catch {
    return getStatusMessage(response.status);
  }
};

const getStreamContextFields = (request: ConnieStreamContext) => ({
  ...(request.sessionId && { sessionId: request.sessionId }),
  ...(request.contactId && { contactId: request.contactId }),
  ...(request.currentJobReference && {
    currentJobReference: request.currentJobReference,
  }),
});

const appendStreamContext = (
  formData: FormData,
  request: ConnieStreamContext
) => {
  const fields = getStreamContextFields(request);
  if (fields.sessionId) {
    formData.append('sessionId', fields.sessionId);
  }
  if (fields.contactId) {
    formData.append('contactId', fields.contactId);
  }
  if (fields.currentJobReference) {
    formData.append('currentJobReference', fields.currentJobReference);
  }
};

const buildRequestInit = (
  request: ConnieStreamRequest,
  token: string,
  signal?: AbortSignal
): RequestInit => {
  if (isVoiceRequest(request)) {
    if (!request.file || request.file.size === 0) {
      throw new ConnieStreamError('Voice note is empty.');
    }

    const formData = new FormData();
    formData.append('file', request.file, request.fileName);
    appendStreamContext(formData, request);

    return {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      body: formData,
      signal,
    };
  }

  const message = request.message.trim();
  if (!message || message.length > 5000) {
    throw new ConnieStreamError(
      'Message must be between 1 and 5000 characters.'
    );
  }

  return {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      message,
      ...getStreamContextFields(request),
      ...(request.prompts?.length && { prompts: request.prompts }),
    }),
    signal,
  };
};

const connieService = {
  async stream(
    request: ConnieStreamRequest,
    onEvent: (event: ConnieStreamEvent) => void | Promise<void>,
    signal?: AbortSignal
  ): Promise<void> {
    const token = storage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new ConnieStreamError('Not authenticated.', 401);
    }

    const response = await fetch(
      CONNIE_STREAM_URLS[request.channel ?? 'global'],
      buildRequestInit(request, token, signal)
    );

    if (!response.ok || !response.body) {
      throw new ConnieStreamError(
        await getJsonErrorMessage(response),
        response.status
      );
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let receivedDone = false;
    let pendingEventName: string | null = null;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() ?? '';

        for (const frame of frames) {
          const xanoEventName = getXanoEventName(frame);
          if (xanoEventName) {
            pendingEventName = xanoEventName;
            continue;
          }

          if (isUnknownSseEvent(frame, isConnieEventName)) {
            continue;
          }

          const event = parseEvent(frame, pendingEventName);
          pendingEventName = null;
          if (!event) continue;

          await onEvent(event);

          if (event.type === 'error') {
            throw new ConnieStreamError(event.data.message);
          }

          if (event.type === 'done') {
            receivedDone = true;
            await reader.cancel();
            return;
          }
        }
      }

      buffer += decoder.decode();
      if (buffer.trim()) {
        const event = parseEvent(buffer, pendingEventName);
        if (event) {
          await onEvent(event);
          if (event.type === 'error') {
            throw new ConnieStreamError(event.data.message);
          }
          if (event.type === 'done') {
            receivedDone = true;
            return;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!receivedDone && !signal?.aborted) {
      throw new ConnieStreamError(
        'Connie ended the response before completing.'
      );
    }
  },
};

export { connieService, parseEvent as parseConnieStreamEvent };
